import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { AppShell, ToolbarDivider, Button } from '@cloudsam/ui';
import SamLogo from './components/SamLogo';
import SummaryPanel from './components/SummaryPanel';
import AttentionPanel from './components/AttentionPanel';
import ChannelsList from './components/ChannelsList';
import ActivityView from './components/ActivityView';
import { buildActivityChart } from './components/activityChart';
import AuditView from './components/AuditView';
import ChannelsToolbarActions from './components/ChannelsToolbarActions';
import BindingsOverlay from './components/BindingsOverlay';
import BindingDefaultsOverlay from './components/BindingDefaultsOverlay';
import SendPreviewPanel from './components/SendPreviewPanel';
import { useAuth } from './contexts/AuthContext';
import { useContextLabel } from './contexts/IdleContext';
import * as api from './utils/channelsApi';
import {
    CHANNELS, channelById, statusTag, credentialHealth, inboundHealth,
    bindingIdentityLine, channelStatusRowMatches,
} from './utils/channelCatalogue';
import { SIGNED_OUT_PREVIEW, WORKING_ORG_ID, WORKING_ORG_LABEL } from './config';
import { getSessionToken } from './utils/session';
import { selectableChannels, defaultSelection } from './sendflow/channelSelector';
import { assessEligibility } from './sendflow/eligibility';
import { priceEstimate } from './sendflow/pricing';
import { sendFailureCopy } from './sendflow/failureCopy';

/**
 * Channels — the fourth SamSurveyor surface.
 *
 * Two things about this screen are deliberate and easy to undo by accident.
 *
 * First, it is standalone. Console carries no link to it yet, and this app
 * carries no link back. Every surface lives in its own tab, and the launcher
 * button in Console is a separate decision that has not been made.
 *
 * Second, where the service cannot answer, the screen says so in words instead
 * of showing an empty box. An empty box means "there is none of this", and that
 * is a different fact from "nobody can tell you yet". Showing the first when the
 * second is true sends somebody looking for a fault that is not there.
 */

// The published US marketing rate, from the rate schedule verified with WP-CH-11.
const REMINDER_PRICE = '$0.0087';

// Said once, by the panel that would otherwise be empty. It used to be printed
// at page level as well, so the same sentence appeared twice on one screen.
const NOT_LOADED_NOTE = 'Not loaded, because this tab is not carrying a session.';

const VIEWS = ['channels', 'activity', 'audit'];

// What each view's button says. 'preview' needs two words to explain itself.
const VIEW_LABELS = { channels: 'Channels', activity: 'Activity', audit: 'Audit' };

function readView() {
    const asked = new URLSearchParams(window.location.search).get('view');
    return VIEWS.includes(asked) ? asked : 'channels';
}

function writeView(view) {
    // Keeping the view in the address makes it linkable. A browser refuses this
    // outright on a page opened straight off the disk, so the failure is caught
    // and the view still changes. Losing the link is a small loss. Losing the
    // view switcher because the address bar objected is not.
    try {
        const url = new URL(window.location.href);
        url.searchParams.set('view', view);
        window.history.replaceState({}, '', url);
    } catch (err) {
        /* opened as a file, so there is no address to update */
    }
}

function money(n) {
    return `$${(Number(n) || 0).toFixed(2)}`;
}

function useDebouncedValue(value, delay = 300) {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delay);
        return () => window.clearTimeout(timer);
    }, [value, delay]);

    return debounced;
}

function emailFormFromConfiguration(configuration = {}) {
    return {
        send_mode: configuration.send_mode || 'platform_shared',
        provider: configuration.provider || 'webhook',
        from_local_part: configuration.from_local_part || 'noreply',
        from_domain: configuration.from_domain || 'samsurveyor.com',
        smtp_host: configuration.smtp_host || '',
        smtp_port: configuration.smtp_port || 587,
        smtp_security: configuration.smtp_security || 'starttls',
        smtp_username: configuration.smtp_username || '',
        smtp_password: '',
        microsoft_tenant_id: configuration.microsoft_tenant_id || '',
        microsoft_mailbox: configuration.microsoft_mailbox || '',
        google_workspace_domain: configuration.google_workspace_domain || '',
        google_sender_mailbox: configuration.google_sender_mailbox || '',
    };
}

export default function App() {
    const { user, isAdmin, roleName } = useAuth();

    const [view, setView] = useState(readView);
    const [bindings, setBindings] = useState([]);
    const [testRecipients, setTestRecipients] = useState([]);
    const [summaryPeriod, setSummaryPeriod] = useState('month');
    const summaryPeriodRef = useRef(summaryPeriod);
    summaryPeriodRef.current = summaryPeriod;
    const [overview, setOverview] = useState(null);
    const [channelControls, setChannelControls] = useState(null);
    const [requestingEnablement, setRequestingEnablement] = useState('');
    const [attention, setAttention] = useState(null);
    const [attentionError, setAttentionError] = useState('');
    const [rateRows, setRateRows] = useState([]);
    const [sends, setSends] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [toast, setToast] = useState('');
    const [search, setSearch] = useState('');

    const [events, setEvents] = useState({ rows: [], total: 0, error: '', loading: false });
    const [audit, setAudit] = useState({ rows: [], total: 0, error: '', loading: false });
    const [eventPage, setEventPage] = useState({ page: 1, rows: 50 });
    const [auditPage, setAuditPage] = useState({ page: 1, rows: 50 });
    const [eventSearch, setEventSearch] = useState('');
    const [auditSearch, setAuditSearch] = useState('');
    const debouncedEventSearch = useDebouncedValue(eventSearch);
    const debouncedAuditSearch = useDebouncedValue(auditSearch);

    // The Bindings overlay: which channel's setup is open, and the Email
    // surface's own state. Email is the first channel with a real surface, so its
    // state lives here until there is a second one to share a shape with.
    const [openChannel, setOpenChannel] = useState(null);
    const [saving, setSaving] = useState(false);
    const [emailMode, setEmailMode] = useState('platform_shared');
    const [emailForm, setEmailForm] = useState({});
    const [emailState, setEmailState] = useState(null);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailTesting, setEmailTesting] = useState(false);
	const [emailTestPassed, setEmailTestPassed] = useState(false);
    const [emailError, setEmailError] = useState('');

    const [settingsFor, setSettingsFor] = useState(null);
    const [settingsValues, setSettingsValues] = useState({});
    const [settingsError, setSettingsError] = useState('');

    const [previewChannel, setPreviewChannel] = useState(null);
    const [failureCode, setFailureCode] = useState('NO_BINDING');

    // Named for the lock screen: whichever channel's setup or settings panel
    // is open, or failing that, which of the three tabs (Channels, Activity,
    // Audit) is showing. If this tab locks, its lock screen says this, so a
    // person can tell what they'd be signing back in to finish.
    const contextLabel = openChannel?.name
        ? `${openChannel.name} setup`
        : settingsFor
            ? `${channelById(settingsFor.channel)?.name || settingsFor.channel} settings`
            : VIEW_LABELS[view] || null;
    useContextLabel(contextLabel);

    // A token pasted into this tab is a real session, so from here on the only
    // question that matters is whether the screen can ask the service anything.
    const [token] = useState(getSessionToken);
    const canLoad = !SIGNED_OUT_PREVIEW || !!token;
    const canAdmin = isAdmin;
    const displayedRoleName = SIGNED_OUT_PREVIEW ? 'Local review' : roleName;

    const say = useCallback((message) => {
        setToast(message);
        window.setTimeout(() => setToast(''), 2600);
    }, []);

    // ── Loading ─────────────────────────────────────────────────────────────

    const loadCore = useCallback(async () => {
        setLoadError('');

        // Nothing to ask for and nobody to ask as. The screen still draws
        // everything that does not need an account.
        if (!canLoad) return;

        try {
            const [b, t] = await Promise.all([
                api.listBindings(),
                api.listTestRecipients(),
            ]);
            setBindings(b);
            setTestRecipients(t);
        } catch (err) {
            setLoadError(err.message || 'The channel list could not be read.');
        }

        // Money and totals are loaded separately and failures are swallowed on
        // purpose. A billing endpoint that is unavailable must not take the
        // binding list down with it, because the binding list is what somebody
        // came here to fix.
        const requestedSummaryPeriod = summaryPeriod;
        try {
            const nextOverview = await api.getChannelOverview(requestedSummaryPeriod);
            if (summaryPeriodRef.current === requestedSummaryPeriod) setOverview(nextOverview);
        } catch (err) {
            if (summaryPeriodRef.current === requestedSummaryPeriod) setOverview(null);
        }
        try {
            setAttention(await api.getChannelAttention());
            setAttentionError('');
        } catch (err) {
            setAttention(null);
            setAttentionError(err.message || 'Operational attention could not be read.');
        }
        try { setSends(await api.sendsByChannel(14)); } catch (err) { setSends([]); }
        try { setChannelControls(await api.getChannelControlStatus()); } catch (err) { setChannelControls(null); }
    }, [canLoad, summaryPeriod]);

    const loadCoreRef = useRef(loadCore);
    loadCoreRef.current = loadCore;

    useEffect(() => { loadCore(); }, [loadCore]);

    useEffect(() => {
        if (!canLoad) return undefined;
        // A dashboard nobody is looking at does not need fresh numbers. The
        // interval skips ticks while the tab is hidden, and the visibility
        // listener refreshes once the moment the tab is looked at again, so a
        // returning reader never sees stale data. Before this gate, every
        // forgotten Channels tab polled sam-dbam around the clock (~8 requests
        // a minute each, ~9,000/day observed in the request logs).
        const tick = () => { if (!document.hidden) loadCore(); };
        const timer = window.setInterval(tick, 60000);
        document.addEventListener('visibilitychange', tick);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [canLoad, loadCore]);

    // Health checks are provider work, not page loading. Start one in the
    // background when the app opens, then serialize future sweeps. A sweep that
    // consumes at least a minute moves to the less expensive 15-minute cadence.
    useEffect(() => {
        if (!canLoad) return undefined;
        let cancelled = false;
        let timer = null;
        const run = async () => {
            const started = Date.now();
            let duration = 0;
            try {
                const result = await api.refreshChannelHealth();
                duration = Number(result?.duration_ms) || (Date.now() - started);
                if (!cancelled) await loadCoreRef.current();
            } catch (err) {
                // The one-minute status read keeps the last completed result on
                // screen. A failed refresh must not erase that evidence.
                console.warn('Channel health refresh failed:', err);
                duration = Date.now() - started;
            }
            if (!cancelled) {
                const delay = duration >= 60000 ? 15 * 60 * 1000 : 10 * 60 * 1000;
                timer = window.setTimeout(run, delay);
            }
        };
        run();
        return () => {
            cancelled = true;
            if (timer) window.clearTimeout(timer);
        };
    }, [canLoad]);

    useEffect(() => {
        if (view !== 'activity' || !canLoad) return undefined;
        let cancelled = false;
        setEvents((current) => ({ ...current, error: '', loading: true }));
        api.listChannelEvents({
            limit: eventPage.rows,
            offset: (eventPage.page - 1) * eventPage.rows,
            search: debouncedEventSearch.trim(),
        })
            .then((body) => {
                if (!cancelled) setEvents({ rows: body?.events || [], total: body?.total || 0, error: '', loading: false });
            })
            .catch((err) => {
                if (!cancelled) setEvents({ rows: [], total: 0, error: err.message || 'The event list could not be read.', loading: false });
            });
        return () => { cancelled = true; };
    }, [view, eventPage, debouncedEventSearch, canLoad]);

    useEffect(() => {
        if (view !== 'audit' || !canLoad) return undefined;
        let cancelled = false;
        setAudit((current) => ({ ...current, error: '', loading: true }));
        api.listChannelAudit({
            limit: auditPage.rows,
            offset: (auditPage.page - 1) * auditPage.rows,
            search: debouncedAuditSearch.trim(),
        })
            .then((body) => {
                if (!cancelled) setAudit({ rows: body?.entries || [], total: body?.total || 0, error: '', loading: false });
            })
            .catch((err) => {
                if (!cancelled) setAudit({ rows: [], total: 0, error: err.message || 'The audit trail could not be read.', loading: false });
            });
        return () => { cancelled = true; };
    }, [view, auditPage, debouncedAuditSearch, canLoad]);

	useEffect(() => {
		const status = emailState?.last_probe?.status;
		if (openChannel?.id !== 'browser' || !canLoad || !['requested', 'sent'].includes(status)) return undefined;
		let cancelled = false;
		const poll = async () => {
			try {
				const state = await api.getEmailChannel();
				if (cancelled) return;
				setEmailState(state);
				if (state.last_probe?.status === 'passed' && state.pending?.verification_state === 'verified') {
					setEmailTestPassed(true);
					say('Email test passed · Save is now available');
				} else if (['failed', 'expired'].includes(state.last_probe?.status)) {
					setEmailTestPassed(false);
					setEmailError(state.last_probe.failure_message || 'The email test did not pass.');
				}
			} catch (err) {
				if (!cancelled) setEmailError(err.message || 'The email test result could not be read.');
			}
		};
		const timer = window.setInterval(poll, 2000);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [openChannel?.id, canLoad, emailState?.last_probe?.status, say]);

    const goto = (next) => { setView(next); writeView(next); };

    // ── Derived ─────────────────────────────────────────────────────────────

    // A period switch must never relabel the old period's values while the new
    // request is in flight. A mismatched snapshot is unavailable, hence dashes.
    const displayedOverview = overview?.period === summaryPeriod ? overview : null;

    const metricsByBinding = useMemo(() => {
        const indexed = {};
        for (const metric of displayedOverview?.bindings || []) {
            indexed[metric.sender_binding_id] = metric;
        }
        return indexed;
    }, [displayedOverview]);

    const controlsByChannel = useMemo(() => {
        const indexed = {};
        for (const control of channelControls || []) indexed[control.channel] = control;
        return indexed;
    }, [channelControls]);

    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return CHANNELS.map((channel) => {
            const persistenceChannel = channel.bindingChannel || channel.id;
            const own = bindings.filter((b) => b.channel === persistenceChannel);
            if (!channelStatusRowMatches(channel, own, term)) return null;

            return {
                channel,
                control: controlsByChannel[persistenceChannel] || {
                    channel: persistenceChannel,
                    enabled: null,
                    runtime_health: 'unknown',
                    enablement_request: { state: 'none' },
                },
                bindings: own.map((b) => {
                    const metric = metricsByBinding[b.binding_id];
                    const credential = credentialHealth(b);
                    return {
                        binding_id: b.binding_id,
                        display_label: b.display_label,
                        identity: bindingIdentityLine(b),
                        isDefault: !!b.is_default,
                        status: statusTag(b.state),
                        statusReason: b.state_reason || '',
                        credential,
                        credentialExpired: b.state === 'credential_expired' || credential.text === 'Expired',
                        inbound: inboundHealth(b),
                        queued: metric?.queued ?? null,
                        knownQueued: metric?.known_queued ?? 0,
                        active: metric?.active ?? null,
                        activeSurveys: metric?.active_surveys ?? null,
                        surveysSent: metric?.surveys_sent ?? null,
                        messagesSent: metric?.messages_sent ?? null,
                        knownSurveysSent: metric?.known_surveys_sent ?? 0,
                        knownMessagesSent: metric?.known_messages_sent ?? 0,
                        spendValue: metric?.spend ?? null,
                        knownSpendValue: metric?.known_spend ?? 0,
                        usage: metric?.messages_sent == null || metric?.surveys_sent == null
                            ? '—'
                            : `${metric.surveys_sent} surveys · ${metric.messages_sent} msgs`,
                        headroom: channel.metered ? '' : 'no per-message cost',
                        spend: metric?.spend == null ? '—' : money(metric.spend),
                        raw: b,
                    };
                }),
            };
        }).filter(Boolean);
    }, [bindings, search, metricsByBinding, controlsByChannel]);

    const activeCount = displayedOverview?.summary?.bindings_active ?? null;
    const provisionedCount = displayedOverview?.summary?.bindings_provisioned ?? null;
    const usageIncomplete = displayedOverview?.summary?.usage_complete === false;
    const periodMessages = displayedOverview?.summary?.messages_sent
        ?? (displayedOverview?.summary?.known_messages_sent > 0 ? `${displayedOverview.summary.known_messages_sent}+` : null);
    const periodSurveys = displayedOverview?.summary?.surveys_sent
        ?? (displayedOverview?.summary?.known_surveys_sent > 0 ? `${displayedOverview.summary.known_surveys_sent}+` : null);
    const periodSpend = displayedOverview?.summary?.spend == null || displayedOverview.summary.spend === 0
        ? null
        : money(displayedOverview.summary.spend);
    const liveNow = displayedOverview?.summary?.active ?? null;

    const summaryPeriodLabel = useMemo(() => {
        if (!displayedOverview?.period_start || !displayedOverview?.period_end) return '—';
        const start = new Date(displayedOverview.period_start);
        const end = new Date(displayedOverview.period_end);
        if (summaryPeriod === 'year_to_date') {
            return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })} UTC`;
        }
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    }, [displayedOverview, summaryPeriod]);

    const sendChart = useMemo(() => buildActivityChart(CHANNELS, sends), [sends]);

    // ── The send preview (WP-CH-12) ─────────────────────────────────────────

    const previewRows = useMemo(() => selectableChannels(bindings), [bindings]);
    const previewSelected = previewChannel || defaultSelection(previewRows);

    // Rate rows exist to price the preview for one channel, so they load when
    // the previewed channel changes — not on the 60-second dashboard poll.
    // (They used to be fetched there with no channel argument, which the
    // billing API answers with 400, so this panel had never shown data.)
    // Rate cards only change when an operator approves a new schedule.
    useEffect(() => {
        if (!canLoad || !previewSelected) {
            setRateRows([]);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const schedules = await api.listRateSchedules(previewSelected);
                const live = schedules[0];
                const rows = live ? await api.listRateRows(live.schedule_id || live.id) : [];
                if (!cancelled) setRateRows(rows);
            } catch (err) {
                if (!cancelled) setRateRows([]);
            }
        })();
        return () => { cancelled = true; };
    }, [canLoad, previewSelected]);

    // Two made-up recipients, and they are labelled as made up on the panel.
    // The screen owns no recipient list, and inventing one that looked real
    // would be worse than showing none.
    const previewEligibility = useMemo(() => {
        if (!previewSelected) return null;
        const sample = [
            { recipient_id: 'sample-1', name: 'Sample recipient', email: 'sample@example.com', phone_number: '+15125550110' },
            { recipient_id: 'sample-2', name: 'Sample with no address', email: '', phone_number: '' },
        ];
        return assessEligibility(sample, [], previewSelected);
    }, [previewSelected]);

    const previewPrice = useMemo(() => {
        if (!previewSelected) return null;
        return priceEstimate(
            { representative: 11, minimum: 9, maximum: 14 },
            rateRows,
            { channel: previewSelected, destinationCountry: 'US' }
        );
    }, [previewSelected, rateRows]);

    // ── Actions ─────────────────────────────────────────────────────────────

    const saveSettings = async () => {
        setSaving(true);
        setSettingsError('');
        try {
            await api.saveBindingDefaults(settingsFor.binding_id, settingsValues);
            say('Defaults saved · the change is audit-logged');
            setSettingsFor(null);
            loadCore();
        } catch (err) {
            setSettingsError(err.message || 'The defaults could not be saved.');
        } finally {
            setSaving(false);
        }
    };

    const requestEnablement = async (channelId) => {
        if (!channelId || requestingEnablement) return;
        setRequestingEnablement(channelId);
        try {
            const updated = await api.requestChannelEnablement(channelId);
            setChannelControls((current) => {
                const existing = (current || []).find((item) => item.channel === channelId) || {};
                const remaining = (current || []).filter((item) => item.channel !== channelId);
                return [...remaining, { ...existing, ...updated, channel: updated.channel || channelId }];
            });
            say(updated.already_enabled
                ? 'This channel is already enabled'
                : updated.enablement_request?.state === 'pending'
                    ? 'Enablement request pending'
                    : 'Enablement request recorded');
        } catch (err) {
            say(err.message || 'Enablement could not be requested.');
        } finally {
            setRequestingEnablement('');
        }
    };

    const toggleDefaultBinding = async (e) => {
        try {
            if (e.target.checked) await api.setDefaultBinding(settingsFor.binding_id);
            else await api.clearDefaultBinding(settingsFor.binding_id);
            await loadCore();
            say(e.target.checked ? 'This is now the default binding' : 'Default binding cleared');
        } catch (err) {
            setSettingsError(err.message || 'The default binding could not be changed.');
        }
    };

    // ── Bindings overlay ────────────────────────────────────────────────────

    const loadEmailSetup = async () => {
        setEmailError('');
        setEmailLoading(true);
		if (!canLoad) {
			setEmailState(null);
			setEmailMode('platform_shared');
			setEmailForm(emailFormFromConfiguration());
			setEmailTestPassed(false);
			setEmailLoading(false);
			return;
		}
        try {
            const state = await api.getEmailChannel();
            const shown = state.pending || state.active || {};
            setEmailState(state);
            setEmailMode(shown.send_mode || 'platform_shared');
            setEmailForm(emailFormFromConfiguration(shown));
			setEmailTestPassed(state.pending?.verification_state === 'verified');
        } catch (err) {
            setEmailState(null);
            setEmailMode('platform_shared');
            setEmailForm(emailFormFromConfiguration());
			setEmailTestPassed(false);
            setEmailError(err.message || 'The email setup could not be read.');
        } finally {
            setEmailLoading(false);
        }
    };

    const openBindingsFor = async (channel) => {
        setOpenChannel(channel);
        if (channel.id === 'browser') await loadEmailSetup();
    };

    const openBindings = async () => {
        await openBindingsFor(channelById('browser'));
    };

    const selectBindingsSection = async (section) => {
        setOpenChannel(section);
        if (section.id === 'browser' || section.id === 'templates') {
            await loadEmailSetup();
        }
    };

    const saveEmailSending = async () => {
		if (!emailTestPassed) return false;
        setSaving(true);
        setEmailError('');
        try {
			const state = emailMode === 'platform_shared'
				? await api.returnEmailToPlatform()
				: await api.activatePendingEmailConfiguration();
            setEmailState(state);
			const shown = state.active || {};
			setEmailMode(shown.send_mode || 'platform_shared');
            setEmailForm(emailFormFromConfiguration(shown));
			setEmailTestPassed(false);
            await loadCore();
			say('Email sending setup saved');
			return true;
        } catch (err) {
            setEmailError(err.message || 'The sending setup could not be saved.');
			return false;
        } finally {
            setSaving(false);
        }
    };

    const testEmailSending = async () => {
        setEmailTesting(true);
        setEmailError('');
		setEmailTestPassed(false);
        try {
			if (emailMode === 'platform_shared') {
				setEmailTestPassed(true);
				say('Default SamSurveyor mail service is ready');
				return;
			}
			const provider = emailMode === 'hosted' ? 'webhook' : emailForm.provider;
			await api.savePendingEmailConfiguration({
				...emailForm,
				send_mode: emailMode,
				provider,
			});
			const state = await api.startEmailProbe();
            setEmailState(state);
            say('Real-path probe sent · waiting for SPF, DKIM and DMARC evidence');
        } catch (err) {
            setEmailError(err.message || 'The real-path probe could not be started.');
        } finally {
            setEmailTesting(false);
        }
    };

	const cancelEmailSending = async () => {
		setEmailError('');
		setEmailTestPassed(false);
		if (!canLoad) {
			setOpenChannel(null);
			return;
		}
		setSaving(true);
		try {
			const state = emailState?.pending
				? await api.discardPendingEmailConfiguration()
				: emailState;
			const shown = state?.active || {};
			setEmailState(state);
			setEmailMode(shown.send_mode || 'platform_shared');
			setEmailForm(emailFormFromConfiguration(shown));
			setOpenChannel(null);
		} catch (err) {
			setEmailError(err.message || 'The pending email changes could not be cancelled.');
		} finally {
			setSaving(false);
		}
	};

	const changeEmailMode = (next) => {
		setEmailMode(next);
		setEmailTestPassed(false);
		setEmailError('');
	};

	const changeEmailField = (name, value) => {
		setEmailForm((current) => ({ ...current, [name]: value }));
		setEmailTestPassed(false);
		setEmailError('');
	};

    const transitionEmailBinding = async (nextState) => {
        const bindingId = emailState?.ec_binding_id;
        if (!bindingId) return;
        setSaving(true);
        setEmailError('');
        try {
            await api.transitionBinding(bindingId, nextState, `${nextState === 'active' ? 'Resumed' : nextState === 'paused' ? 'Paused' : 'Disabled'} from the Bindings manager`);
            await loadEmailSetup();
            say(nextState === 'active' ? 'Email binding resumed' : nextState === 'paused' ? 'Email binding paused' : 'Email binding disabled');
        } catch (err) {
            setEmailError(err.message || 'The email binding state could not be changed.');
        } finally {
            setSaving(false);
        }
    };

    const stopEmailBinding = async () => {
        const bindingId = emailState?.ec_binding_id;
        if (!bindingId) return;
        setSaving(true);
        setEmailError('');
        try {
            const result = await api.stopBinding(bindingId);
            await loadEmailSetup();
            const count = Number(result?.cancelled_messages || 0);
            say(count ? `Email stopped · ${count} queued ${count === 1 ? 'message' : 'messages'} cancelled` : 'Email stopped · no queued messages');
        } catch (err) {
            setEmailError(err.message || 'The email binding could not be stopped.');
        } finally {
            setSaving(false);
        }
    };

    const rollbackEmailSending = async () => {
        setSaving(true);
        setEmailError('');
        try {
            const state = await api.rollbackEmailConfiguration();
            setEmailState(state);
            const shown = state.active || {};
            setEmailMode(shown.send_mode || 'platform_shared');
            setEmailForm(emailFormFromConfiguration(shown));
            await loadCore();
            say('The previous certified email route is active again');
        } catch (err) {
            setEmailError(err.message || 'The previous email route could not be restored.');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (openChannel?.id !== 'browser' || !['requested', 'sent'].includes(emailState?.last_probe?.status)) return undefined;
        const timer = window.setInterval(async () => {
            try {
                const state = await api.getEmailChannel();
                setEmailState(state);
                if (state.last_probe?.status === 'passed') say('Email route certified and now active');
            } catch (_) { /* the visible state remains the last confirmed read */ }
        }, 3000);
        return () => window.clearInterval(timer);
    }, [openChannel, emailState?.last_probe?.status, say]);

    // ── Shell ───────────────────────────────────────────────────────────────

    const toolbarLeft = <Button variant="onDark" ghost onClick={() => { loadCore(); say('Channel health refreshed'); }}>Refresh</Button>;

    const toolbarCentre = (
        <>
            <ToolbarDivider />
            {VIEWS.map((v) => (
                <Button key={v} variant="onDark" ghost active={view === v} onClick={() => goto(v)}>
                    {VIEW_LABELS[v]}
                </Button>
            ))}
        </>
    );

    // The role is session-derived and display-only. Mutating controls use the
    // same role to explain their disabled state, while DBAM enforces the
    // permission again on every request.
    const toolbarRight = (
        <ChannelsToolbarActions
            roleName={displayedRoleName}
            bindingsOpen={Boolean(openChannel)}
            onOpenBindings={openBindings}
        />
    );

    return (
        <AppShell
            surface="channels"
            wordmark="Channels"
            logo={<SamLogo size={26} variant="light" />}
            toolbarLeft={toolbarLeft}
            toolbarCentre={toolbarCentre}
            toolbarRight={toolbarRight}
            contentClassName="channels-content"
            className="channels-shell"
            help={(
                <Button variant="onDark" ghost>
                    <HelpCircle size={15} strokeWidth={1.5} />
                    Help
                </Button>
            )}
        >
            {loadError && <div className="panel-error">{loadError}</div>}

            {view === 'channels' && (
                <>
                    <div className="channels-summary-row">
                        <SummaryPanel
                            period={summaryPeriod}
                            periodLabel={summaryPeriodLabel}
                            onPeriodChange={setSummaryPeriod}
                            bindingsActive={activeCount}
                            bindingsTotal={provisionedCount}
                            messagesSent={periodMessages}
                            surveyCount={periodSurveys}
                            spend={periodSpend}
                            liveNow={liveNow}
                            usageIncomplete={usageIncomplete}
                        />
                        <AttentionPanel
                            items={attention}
                            error={attentionError}
                            canAct={canAdmin}
                            onAct={(item) => say(`${item.action} is not wired up yet — the channel drivers are WP-CH-15's open half.`)}
                        />
                    </div>

                    <ChannelsList
                        rows={rows}
                        search={search}
                        onSearch={(e) => setSearch(e.target.value)}
                        footerQueued={displayedOverview?.summary?.queued
                            ?? (displayedOverview?.summary?.known_queued > 0 ? `${displayedOverview.summary.known_queued}+` : null)}
                        footerActive={displayedOverview?.summary?.active ?? null}
                        requestingEnablement={requestingEnablement}
                        onRequestEnablement={requestEnablement}
                    />
                </>
            )}

            {view === 'preview' && (
                <div className="view-fill">
                    <SendPreviewPanel
                        rows={previewRows}
                        selected={previewSelected}
                        onSelect={setPreviewChannel}
                        eligibility={previewEligibility}
                        price={previewPrice}
                        failureCode={failureCode}
                        failureExample={sendFailureCopy(failureCode)}
                        onFailureCode={(e) => setFailureCode(e.target.value)}
                    />
                </div>
            )}

            {view === 'activity' && (
                <div className="view-fill">
                    <ActivityView
                        sends={sendChart.rows}
                        scaleCeiling={sendChart.ceiling}
                        events={events.rows}
                        total={events.total}
                        page={eventPage.page}
                        rows={eventPage.rows}
                        onPage={(page) => setEventPage((p) => ({ ...p, page }))}
                        onRows={(e) => setEventPage({ page: 1, rows: Number(e.target.value) })}
                        search={eventSearch}
                        onSearch={(e) => {
                            setEventSearch(e.target.value);
                            setEventPage((current) => ({ ...current, page: 1 }));
                        }}
                        error={events.error}
                        loading={events.loading}
                        unavailableNote={canLoad ? '' : NOT_LOADED_NOTE}
                    />
                </div>
            )}

            {view === 'audit' && (
                <div className="view-fill">
                    <AuditView
                        entries={audit.rows}
                        total={audit.total}
                        page={auditPage.page}
                        rows={auditPage.rows}
                        onPage={(page) => setAuditPage((p) => ({ ...p, page }))}
                        onRows={(e) => setAuditPage({ page: 1, rows: Number(e.target.value) })}
                        search={auditSearch}
                        onSearch={(e) => {
                            setAuditSearch(e.target.value);
                            setAuditPage((current) => ({ ...current, page: 1 }));
                        }}
                        error={audit.error}
                        loading={audit.loading}
                        unavailableNote={canLoad ? '' : NOT_LOADED_NOTE}
                    />
                </div>
            )}

            {openChannel && (
                <BindingsOverlay
                    channel={openChannel}
                    canAdmin={canAdmin}
                    onClose={openChannel.id === 'browser' ? cancelEmailSending : () => setOpenChannel(null)}
                    onSelectChannel={selectBindingsSection}
                    organizationLabel={WORKING_ORG_LABEL}
                    email={{
                        mode: emailMode,
						onMode: changeEmailMode,
                        form: emailForm,
                        state: emailState,
                        loading: emailLoading,
                        testing: emailTesting,
						testPassed: emailTestPassed,
						canTest: canLoad,
						canManage: canLoad,
						onField: changeEmailField,
                        dnsRecords: [],
                        dnsResult: null,
                        onCheckDns: () => {},
                        onSaveSending: saveEmailSending,
                        onTestSending: testEmailSending,
						onTransitionBinding: transitionEmailBinding,
						onStopBinding: stopEmailBinding,
						onCancelSending: cancelEmailSending,
                        onRollbackSending: rollbackEmailSending,
                        saving,
                        error: emailError,
                        organizationId: WORKING_ORG_ID || user?.org_id,
                        onNotice: say,
                    }}
                />
            )}

            {settingsFor && (
                <BindingDefaultsOverlay
                    binding={settingsFor}
                    defaults={settingsValues}
                    canAdmin={canAdmin}
                    onField={(name, value) => setSettingsValues((v) => ({ ...v, [name]: value }))}
                    onSave={saveSettings}
                    onClose={() => setSettingsFor(null)}
                    saving={saving}
                    error={settingsError}
                    testRecipients={testRecipients.filter((t) => t.channel === settingsFor.channel)}
                    isDefaultBinding={!!settingsFor.is_default}
                    onMakeDefault={toggleDefaultBinding}
                    reminderPrice={REMINDER_PRICE}
                />
            )}

            {toast && <div className="toast">{toast}</div>}
        </AppShell>
    );
}
